import { Component, EventEmitter, OnChanges, Input, Output, SimpleChanges, Type, ElementRef, forwardRef, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Subscription, BehaviorSubject, Subject, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { RGBToHSL } from './color.util'
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatDialog } from '@angular/material/dialog';
import { ColumnsSelectComponent } from './columns-select.component/columns-select.component';
import { ENTER } from '@angular/cdk/keycodes';
import { NG_VALUE_ACCESSOR, FormControl, ControlValueAccessor } from '@angular/forms';
import { DataAdapter } from './datasource/data.adapter';


import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatTable } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { NormalizedItem } from './datasource/model';

export type PipeDescriptor = { pipe: Type<any>, args: string[] }
export type PipesDescriptor = { [column: string]: PipeDescriptor | Type<any> };

export type ColumnDescriptor = {
	header?: string,
	width?: number,
	visible?: boolean,
	sticky?: 'start' | 'end'
	sortDisabled?: boolean,
	pipe?: PipeDescriptor | Type<any>
}
export type ColumnsDescriptor = { [key: string]: ColumnDescriptor | 1 | 0 };
type ColumnsDescriptorStrict = { [key: string]: ColumnDescriptor };

export type FilterDescriptor = { [key: string]: string } & { terms?: string[] };
export type ActionDescriptor = {
	name: string,
	text?: string,
	icon?: string,
	color?: string,
	tip?: string,

	bulk?: boolean,
	menu?: boolean,
	header?: boolean,

	disabled?: boolean //per command
};
export type ActionsDescriptor = ActionDescriptor[];
export type ActionEvent<T = any> = { action: ActionDescriptor, data: T[] };

@Component({
	selector: 'data-base',
	template: '',
	styles: []
})
export class DataComponentBase<T = any> implements ControlValueAccessor {


	readonly separatorKeysCodes: number[] = [ENTER];
	loading = true;
	firstLoad = true;
	@Input() showSearch = true;
	@Input() noDataImage: string;
	@Input('add-url') addUrl: string;
	@Input('show-add') showAdd: boolean;
	@Output() add = new EventEmitter<any>();

	@Input() minAllowed: number;
	@Input() maxAllowed = 1;

	



	@Input() name = '';
	@Input() adapter: DataAdapter<T>;
	normalized$sub: Subscription;


	q$ = new BehaviorSubject<string>(null);


	dataSourceSubscription: Subscription;
	get q() { return this.q$.value; }
	set q(value: string) { this.q$.next(value); }

	filter$ = new Subject<string>();


	@Input() focusedItem: T;
	@Output() focusedItemChange = new EventEmitter<T>();
	@Output() itemClick = new EventEmitter<T>();



	@Input() filterdebounceTime = 800;

	ngOnInit() {
		this.q$.pipe(filter(x => x !== null), debounceTime(+this.filterdebounceTime), takeUntil(this.destroy$))
			.subscribe(x => this.filter$.next(x));

		this.filter$.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe(x => this.onFilter(x));


	}

	async ngOnChanges(changes: SimpleChanges) {

		if (changes['adapter']) {
			console.log(`%c adapter changed! (${this.name})`, 'background: #222; color: #bada55', this.constructor.name);
			if (this.normalized$sub) this.normalized$sub.unsubscribe();
			this.normalized$sub = this.adapter.normalized$.subscribe(data => this.onDataChange(data));
		}
		if (changes['maxAllowed']) this.maxAllowed = +this.maxAllowed;
		if (changes['minAllowed']) this.minAllowed = +this.minAllowed;
		if (changes['showSearch']) this.showSearch = this.showSearch === true || this.showSearch === <any>'true' || this.showSearch === <any>'';
		if (changes['control'] && this.control) {
			this.value = this.control.value; //read value from control (but why not write value to control?)
			this.control.registerOnChange(value => this.value = value);
		}

		// if (changes['value']) { } not needed
	}

	dataChangeListeners: ((data: NormalizedItem<T>[]) => void)[] = [];
	onDataChange(data: NormalizedItem<T>[]) {
		if (this.firstLoad) setTimeout(() => this.firstLoad = false, 1000); //delay this to give time to html template to switch
		this.loading = false;
		this.dataChangeListeners.forEach(x => x(data));
		this._updateViewModel();
	}

	destroy$ = new Subject();
	ngOnDestroy() {
		if (this.normalized$sub) this.normalized$sub.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
		this.adapter.destroy();
	}




	onPageChange(page: PageEvent) {
		this.loading = true;
		this.adapter.page = page;
		//this.selectionModel.clear();
	}

	onSortData(sort: Sort) {
		this.loading = true;
		this.adapter.sort = sort;
		//this.selectionModel.clear();

	}

	onFilter(q: string) {
		//this.selectionModel.clear();
		if (this.q != q) this.q = q;
		this.loading = true;
		this.adapter.filter = this.adapter.normalizeFilter(q) || {};
	}

	//todo grouping https://docs.mongodb.com/manual/reference/operator/aggregation/group/
	isGroup(row: any): boolean { return row.group };


	@ViewChild(MatTable) table: MatTable<T>;
	@Output() rowDropped = new EventEmitter();
	drop(event: CdkDragDrop<any[]>) {
		this.table.renderRows();
		this.rowDropped.emit({ event, from: event.previousIndex, to: event.currentIndex });

		// const prevIndex = this.adapter.normalized.findIndex((d) => d === e.event.item.data);
		// moveItemInArray(this.adapter.normalized, prevIndex, e.event.currentIndex);

	}
	onAdd() { this.add.emit(); }


	//selection
	selectionModel = new SelectionModel<any>(true, [], false);
	toggleSelectAll() {
		if (this.selectionModel.selected.length == this.adapter.normalized.length) this.selectionModel.clear()
		else this.selectionModel.select(this.adapter.normalized.map(n => n.value));

		this._value = this.maxAllowed !== 1 ? this.selectionModel.selected : this.selectionModel.selected[0];
		this._propagateChange();
	}

	select(value: any) {
		this.selectionModel.select(value);
		this._value = this.maxAllowed !== 1 ? this.selectionModel.selected : this.selectionModel.selected[0];
		this._propagateChange();
	}
	deselect(value: any) {
		this.selectionModel.deselect(value);
		this._value = this.maxAllowed !== 1 ? this.selectionModel.selected : this.selectionModel.selected[0];
		this._propagateChange();
	}
	toggle(value: any) {
		if (this.selectionModel.isSelected(value)) this.deselect(value);
		else this.select(value);
	}

	nextFocusedItem() {

		const i = this.focusedItem ? this.adapter.dataSource.data.indexOf(this.focusedItem) : -1;
		this.focusedItem = this.adapter.normalized[i + 1]?.item;
		this.focusedItemChange.emit(this.focusedItem);
	}
	prevFocusedItem() {
		const i = this.focusedItem ? this.adapter.dataSource.data.indexOf(this.focusedItem) : this.adapter.normalized.length;
		this.focusedItem = this.adapter.normalized[i - 1]?.item;
		this.focusedItemChange.emit(this.focusedItem);
	}

	onLongPress(row: NormalizedItem<T>) {
		this.focusedItem = row.item;
		this.selectionModel.toggle(row.value);
		this.longPressed = row; //to notify click about it
	}
	longPressed: NormalizedItem<T>;
	onClick(row: NormalizedItem<T>) {
		this.focusedItem = row.item;
		this.focusedItemChange.emit(this.focusedItem);

		if (this.longPressed !== null) {
			if (this.longPressed != row && this.selectionModel.selected.length > 0) this.selectionModel.toggle(row.value);
			else this.itemClick.emit(this.focusedItem);
		}
		this.longPressed = null; //clear long press notification
	}


	_updateViewModel() {
		const value = this._value;

		this.selectionModel.clear();
		if (value != null) {
			if (Array.isArray(value)) this.selectionModel.select(...value);
			else this.selectionModel.select(value);
		}
	}

	_propagateChange() {
		const value = this._value;
		if (this._onChange) this._onChange(value); //ngModel/ngControl notify (value accessor)
		if (this.control) this.control.setValue(value); //control notify
		this.valueChange.emit(value); //value event binding notify
		this.value$.next(value); //template reference #component.value$ | async
	};


	@Input() control: FormControl = new FormControl();
	@Output() valueChange = new EventEmitter<any | any[]>();
	value$ = new ReplaySubject<Partial<T> | Partial<T>[]>(1);

	_value: Partial<T> | Partial<T>[];
	@Input()
	public get value(): Partial<T> | Partial<T>[] { return this._value; }
	public set value(v: Partial<T> | Partial<T>[]) {
		if (v === this._value) return;
		this._value = v;
		this._updateViewModel();
	}




	//ControlValueAccessor
	_onChange: ((value: any | any[]) => void);
	_onTouch: (() => void);

	writeValue(value: Partial<T> | Partial<T>[]): void { this.value = value; }
	onTouch() { this.control.markAsTouched(); if (this._onTouch) this._onTouch(); };
	registerOnChange(fn: (value: Partial<T> | Partial<T>[]) => void): void { this._onChange = fn; }
	registerOnTouched(fn: () => void): void { this._onTouch = fn; }
	setDisabledState?(isDisabled: boolean): void {
		if (isDisabled && this.control.enabled) this.control.disable();
		else if (isDisabled === false && this.control.disabled) this.control.enable();
	}

}



@Component({
	selector: 'data-table',
	templateUrl: './data-table.component.html',
	styleUrls: ['./data-table.component.css'],
	providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DataTableComponent), multi: true, }]
})
export class DataTableComponent extends DataComponentBase implements OnChanges {

	@ViewChild('table') table: MatTable<any>;

	@Input() accent: string;
	@Input() primary: string;
	@Input() zebra = true;
	@Input() actions: ActionDescriptor[] = [];
	@Input() hasHeader = false;
	@Input() dragDropDisabled: boolean = true;

	@Output() action = new EventEmitter<ActionEvent>();

	bulkActions: ActionDescriptor[];
	headerActions: ActionDescriptor[];

	@Input() pageSizeOptions = [10, 25, 50, 100];


	_properties: ColumnsDescriptorStrict = {}; //only data columns
	_columns: string[] = [];
	@Input() columns: string[] | ColumnsDescriptor | 'auto' = 'auto'; //eventually columns are the container of all and it's a dictionary
	@Input() templates: any = {};

	@Input() cellTemplate: any;
	@ViewChild('defaultTemplate') defaultTemplate: any;



	ngAfterViewInit(): void {
		if (!this.cellTemplate) this.cellTemplate = this.defaultTemplate;
	}

	constructor(protected host: ElementRef<HTMLElement>, protected breakpointObserver: BreakpointObserver, protected dialog: MatDialog) {
		super();
	}


	handset: boolean;

	ngOnInit() {
		super.ngOnInit();
		this.dataChangeListeners.push(data => { if (this.columns == 'auto') this.generateColumns(); });

		this.breakpointObserver.observe([Breakpoints.Handset]).pipe(takeUntil(this.destroy$))
			.subscribe(result => { this.handset = result.matches; });
	}


	async ngOnChanges(changes: SimpleChanges) {

		if (changes['zebra']) this.zebra = changes['zebra'].currentValue === 'true' || changes['zebra'].currentValue === true; //to boolean
		if (changes['accent'] && this.accent) {
			const r = parseInt('0x' + this.accent.substr(1, 2));
			const g = parseInt('0x' + this.accent.substr(3, 2));
			const b = parseInt('0x' + this.accent.substr(5, 2));
			const { h, s, l } = RGBToHSL({ r, g, b });

			this.host.nativeElement.style.setProperty('--accent-hsl', `${h},${s}%,${l}%`);
			this.host.nativeElement.style.setProperty('--highlight-hsl', `${h},${s}%,${l + 20}%`);
			this.host.nativeElement.style.setProperty('--zebra-hsl', `${h},${s}%,${l + 30}%`);
		}
		if (changes['primary'] && this.primary) {
			const r = parseInt('0x' + this.primary.substr(1, 2));
			const g = parseInt('0x' + this.primary.substr(3, 2));
			const b = parseInt('0x' + this.primary.substr(5, 2));
			const { h, s, l } = RGBToHSL({ r, g, b });
			this.host.nativeElement.style.setProperty('--primary-hsl', `${h},${s}%,${l}%`);
		}
		if (changes['actions']) {
			this.generateColumns();
			this.bulkActions = this.actions.filter(a => a.bulk === true);
			this.headerActions = this.actions.filter(a => a.header === true);
		}
		if (changes['columns']) this.generateColumns();
		if (changes['hasHeader']) this.hasHeader = this.hasHeader === true || this.hasHeader === <any>'true' || this.hasHeader === <any>'';


		await super.ngOnChanges(changes);
	}

	private generateColumns() {
		if (this.columns == 'auto') {
			this._properties = {};
			if (this.adapter.normalized && this.adapter.normalized.length) {
				const columns: any = {};
				this.adapter.normalized.forEach(x => Object.keys(x.item).forEach(k => columns[k] = 1));
				Object.keys(columns).forEach(k => { if (!k.startsWith('_')) this._properties[k] = {}; });
			}
		}
		else if (Array.isArray(this.columns)) {
			this._properties = {};
			this.columns.forEach(k => { this._properties[k] = {}; });
		}
		else {
			this._properties = {};
			Object.keys(this.columns).forEach(k => {
				if (this.columns[k] === 1) this._properties[k] = {};
				else if (this.columns[k] === 0) this._properties[k] = { visible: false };
				else this._properties[k] = this.columns[k]
			});
		}


		this._columns = [];

		const selectCol = this._properties.select
		const iCol = this._properties.i;
		const actionsCol = this._properties.actions;
		delete this._properties.select;
		delete this._properties.i;
		delete this._properties.actions;

		if (iCol && iCol.visible !== false) this._columns.push('i');
		if (selectCol == undefined || selectCol.visible !== false) this._columns.push('select');


		this._columns.push(...Object.keys(this._properties));

		if (this.actions && (actionsCol == undefined || actionsCol.visible !== false)) {
			this._columns.push('actions');
		}
	}

	async openColumnsSelectDialog() {
		await this.dialog.open(ColumnsSelectComponent, { data: this._properties, width: '60%' })
			.afterClosed()
			.toPromise();
	}


	//actions
	actionsMap = new Map<any, ActionDescriptor[]>();
	actionsMenuMap = new Map<any, ActionDescriptor[]>();
	getActions(x: any): ActionDescriptor[] {
		let actions = this.actionsMap.get(x);
		if (!actions) {
			actions = this.actions.filter(a => a.bulk !== true && a.menu !== true && a.header !== true).map(a => Object.assign({}, a));
			this.actionsMap.set(x, actions);
		}
		return actions;
	}
	getMenuActions(x: any): ActionDescriptor[] {
		let actions = this.actionsMenuMap.get(x);
		if (!actions) {
			actions = this.actions.filter(a => a.menu === true).map(a => Object.assign({}, a));
			this.actionsMenuMap.set(x, actions);
		}
		return actions;
	}
	onAction(action: any, data: NormalizedItem[]) {
		//TODO should action set loading automatically just like filter?
		this.action.emit({ action, data: data['item'] || data });
	}

}

